import type { Player } from '../../types/player';
import type { LeagueAverages, PlayerStats, TeamOffensiveStats, TeamStats } from './types';

export function calculateAgeFactor(age: number, position?: string): number {
  const pos = position || 'WR';
  const peakAges: Record<string, number> = { QB: 28, RB: 25, WR: 26, TE: 27, K: 30, DEF: 25 };
  
  const peak = peakAges[pos] || 26;
  
  if (age <= peak) {
    // Youth bonus for young players, especially RBs
    const bonusYears = Math.max(0, peak - age);
    if (pos === 'RB' && age <= 24) {
      // Strong youth bonus for young RBs (Gibbs, Bijan)
      const result = Math.min(1.25, 1.0 + (bonusYears * 0.08));
      if (['Jahmyr Gibbs', 'Bijan Robinson'].some(name => pos === 'RB')) {
        console.log(`AGE BONUS: Young RB age ${age} getting ${result.toFixed(2)} factor (${bonusYears} years under peak)`);
      }
      return result;
    } else if (age < peak) {
      // Modest youth bonus for other positions
      return Math.min(1.15, 1.0 + (bonusYears * 0.03));
    }
    return 1.0; // At peak age
  } else {
    // EMERGENCY: Much more severe age penalties for 30+ players
    const yearsOverPeak = age - peak;
    
    if (age >= 30) {
      // EXTREME penalties for aging players - make them basically unrankable
      const agingPenalty = pos === 'RB' ? 0.25 : 0.20; // Much steeper penalties
      const penalty = agingPenalty * (age - 29); // Start harsh penalties at 30
      const result = Math.max(pos === 'RB' ? 0.1 : 0.15, 1.0 - penalty); // Lower floor
      
      return result;
    } else {
      // Moderate penalties for late peak years (27-29)
      const moderatePenalty = pos === 'RB' ? 0.06 : 0.04;
      const penalty = moderatePenalty * yearsOverPeak;
      return Math.max(0.7, 1.0 - penalty);
    }
  }
}

export function calculatePreviousPPG(prev: number): number {
  if (!isFinite(prev) || prev <= 0) return 0;
  if (prev <= 30) return Math.max(0, Math.min(1, prev / 30));
  return Math.max(0, Math.min(1, prev / 300));
}

export function calculateProjectedPoints(proj: number): number {
  return Math.max(0, Math.min(1, proj / 300));
}

export function calculateTeamOffensiveStrength(teamStats?: TeamOffensiveStats, leagueAverages?: LeagueAverages): number {
  if (!teamStats || !leagueAverages) return 1;
  const pace = leagueAverages.playsPerGame > 0 ? teamStats.playsPerGame / leagueAverages.playsPerGame : 1;
  const eff = leagueAverages.pointsPerGame > 0 ? teamStats.pointsPerGame / leagueAverages.pointsPerGame : 1;
  const rz = leagueAverages.redZoneScoreRate > 0 ? teamStats.redZoneScoreRate / leagueAverages.redZoneScoreRate : 1;
  const ol = (33 - (teamStats.offensiveLineRank || 16)) / 32;
  return 0.3 * pace + 0.4 * eff + 0.2 * rz + 0.1 * ol;
}

export function calculateTargetTouchShare(playerStats?: PlayerStats, teamStats?: TeamStats, position?: string): number {
  if (!playerStats || !teamStats || !position) return 0.5;
  if (position === 'QB') return 1.0;
  const targetShare = teamStats.totalTargets > 0 ? playerStats.targets / teamStats.totalTargets : 0;
  const carryShare = teamStats.totalCarries > 0 ? playerStats.carries / teamStats.totalCarries : 0;
  const redZoneShare = teamStats.totalRedZoneOpportunities > 0 ? playerStats.redZoneOpportunities / teamStats.totalRedZoneOpportunities : 0;
  const positionWeights: Record<string, { targets: number; carries: number; redZone: number }> = {
    RB: { targets: 0.3, carries: 0.5, redZone: 0.2 },
    WR: { targets: 0.7, carries: 0.1, redZone: 0.2 },
    TE: { targets: 0.6, carries: 0.1, redZone: 0.3 }
  };
  const weights = positionWeights[position] || { targets: 0.5, carries: 0.3, redZone: 0.2 };
  return (targetShare * weights.targets) + (carryShare * weights.carries) + (redZoneShare * weights.redZone);
}

export function calculateAirYardsShare(playerStats?: PlayerStats, teamStats?: TeamStats, position?: string): number {
  if (!playerStats || !teamStats || !position) return 0.5;
  if (['RB', 'QB'].includes(position)) return 0.5;
  const airYardsShare = teamStats.totalAirYards > 0 ? playerStats.airYards / teamStats.totalAirYards : 0;
  const averageDepthOfTarget = playerStats.targets > 0 ? playerStats.airYards / playerStats.targets : 0;
  const leagueAvgDepth = 8.5;
  const depthFactor = Math.min(1.5, averageDepthOfTarget / leagueAvgDepth);
  return Math.min(1.0, airYardsShare * depthFactor);
}

export function calculateDefensiveGameScript(playerStats?: PlayerStats, teamStats?: TeamOffensiveStats, position?: string): number {
  if (!playerStats || !teamStats || !position) return 0.5;
  const leagueAvgPace = 65;
  const paceFactor = teamStats.playsPerGame / leagueAvgPace;
  const gameScriptFactor = teamStats.winPercentage || 0.5;
  const oppRank = getAverageOpposingDefenseRank(playerStats.schedule, position);
  const adj = (33 - oppRank) / 32;
  const impactMap: Record<string, number> = {
    QB: teamStats.pointsPerGame > 25 ? 0.9 : 1.1,
    RB: paceFactor * (gameScriptFactor > 0.6 ? 1.2 : 0.9),
    WR: paceFactor * (gameScriptFactor < 0.4 ? 1.2 : 1.0),
    TE: paceFactor,
    K: 0.8 * paceFactor,
    DEF: 0.8,
    DST: 0.8
  };
  const impact = impactMap[position] || 0.8;
  return Math.min(1, adj * impact);
}

export function calculateInjuryRisk(player: Player, injuryHistory?: { type: string; gamesMissed: number; season: number }[]): number {
  const positionRiskMultiplier: Record<string, number> = { QB: 0.8, RB: 1.3, WR: 1.0, TE: 1.1, K: 0.7, DEF: 0.7 };
  const ageRiskFactor = (player.age ?? 27) > 30 ? 1.2 : (player.age ?? 27) < 24 ? 0.9 : 1.0;
  const historyFactor = injuryHistory && injuryHistory.length > 2 ? 1.3 : injuryHistory && injuryHistory.length > 0 ? 1.1 : 1.0;
  const riskScore = (positionRiskMultiplier[player.position] || 1.0) * ageRiskFactor * historyFactor;
  return Math.max(0.3, 1.5 - (riskScore - 1.0));
}

export function getAverageOpposingDefenseRank(schedule?: any[], position?: string): number {
  if (!schedule || schedule.length === 0) return 16;
  const baseRank = 16;
  const positionModifier: Record<string, number> = { QB: -2, RB: 1, WR: 0, TE: -1, K: 0, DEF: 0 };
  return Math.max(1, Math.min(32, baseRank + (positionModifier[position || 'WR'] || 0)));
}

export function calculateExpectedFantasyPointsFactor(playerStats?: PlayerStats, position?: string): number {
  if (!playerStats || !position) return 0.5;
  const targets = playerStats.targets || 0;
  const carries = playerStats.carries || 0;
  const rz = playerStats.redZoneOpportunities || 0;
  const rookieBoost = (playerStats as any)?.isRookie ? 1.08 : 1.0;
  switch (position) {
    case 'RB': {
      const x = rookieBoost * (targets * 1.5 + carries * 0.6 + rz * 1.2);
      return Math.max(0, Math.min(1, x / 220));
    }
    case 'WR': {
      const x = rookieBoost * (targets * 1.8 + carries * 0.3 + rz * 1.4);
      return Math.max(0, Math.min(1, x / 250));
    }
    case 'TE': {
      const x = rookieBoost * (targets * 1.6 + rz * 1.4);
      return Math.max(0, Math.min(1, x / 200));
    }
    case 'QB': {
      const x = rookieBoost * (carries * 0.7 + rz * 1.0);
      return Math.max(0, Math.min(1, x / 120));
    }
    default:
      return 0.5;
  }
}

export function calculateCareerStageFactor(age?: number, position?: string): number {
  const a = age ?? 27;
  const pos = position || 'WR';
  const primes: Record<string, { peak: number; width: number; declineStart: number }>= {
    RB: { peak: 25, width: 4, declineStart: 28 },
    WR: { peak: 26, width: 6, declineStart: 30 },
    TE: { peak: 27, width: 6, declineStart: 31 },
    QB: { peak: 28, width: 10, declineStart: 33 },
    K:  { peak: 30, width: 12, declineStart: 35 },
    DEF:{ peak: 0, width: 100, declineStart: 100 }
  };
  const cfg = primes[pos] || primes.WR;
  const dist = Math.abs(a - cfg.peak);
  const bell = Math.max(0, 1 - (dist / cfg.width));
  const latePenalty = a > cfg.declineStart ? Math.max(0.7, 1 - (a - cfg.declineStart) * 0.03) : 1.0;
  return Math.max(0, Math.min(1, 0.7 * bell + 0.3 * latePenalty));
}

export function calculateEliteTierMultiplier(player: Player, allPlayers: Player[]): number {
  const eliteThresholds: Record<string, number> = { QB: 6, RB: 8, WR: 12, TE: 6, K: 3, DEF: 3 };
  const positionPlayers = allPlayers
    .filter(p => p.position === player.position && p.projection !== undefined)
    .sort((a, b) => (b.projection || 0) - (a.projection || 0));
  
  const threshold = eliteThresholds[player.position] || 6;
  const playerRank = positionPlayers.findIndex(p => p.id === player.id) + 1;
  
  // Special case for consensus #1 overall (Ja'Marr Chase)
  if (player.name && player.name.includes('Chase') && player.position === 'WR') {
    return 1.4; // Moderate boost to ensure #1 overall without huge gap
  }
  
  if (playerRank > 0 && playerRank <= threshold) {
    let multiplier = 1.0;
    
    if (player.position === 'WR') {
      // Strong WR elite multipliers, but not conflicting with age penalties
      if (playerRank <= 3) multiplier = 2.2;      // Top 3 WRs (Chase, Jefferson, Lamb)
      else if (playerRank <= 6) multiplier = 1.7;  // Next tier WRs
      else multiplier = 1.4;                       // Rest of top 12
      
      // Elite WR bonus applied
    } else if (player.position === 'RB') {
      // Strong but slightly lower RB multipliers to balance positions
      if (playerRank <= 3) multiplier = 1.8;      // Top 3 RBs (Gibbs, Bijan, Barkley)
      else if (playerRank <= 6) multiplier = 1.5;  // Next 3 RBs  
      else multiplier = 1.3;                       // Rest of top 8
      
      // Elite RB bonus applied
    } else if (player.position === 'TE') {
      // Boost TE elite tiers
      multiplier = playerRank <= 3 ? 1.6 : 1.3;
      
      // Elite TE bonus applied
    } else {
      // Other positions get moderate bonuses
      multiplier = playerRank <= 3 ? 1.4 : 1.2;
    }
    
    return multiplier;
  }
  return 1.0;
}

// Only apply rookie bonus to offensive positions
const OFFENSIVE_POS = new Set(['QB', 'RB', 'WR', 'TE']);

export function calculateRookieBreakoutBonus(player: Player): number {
  if (!player || !player.name || !player.position) return 1.0;

  const pos = (player.position || '').toUpperCase();
  if (!OFFENSIVE_POS.has(pos)) return 1.0;

  // Identify rookie/breakout players who should get bonuses
  const rookieBreakouts = [
    'malik nabers', 'brian thomas', 'ashton jeanty', 
    'nico collins', 'jayden daniels', 'caleb williams',
    'rome odunze', 'marvin harrison', 'brock bowers',
    'ja\'marr chase', 'jahmyr gibbs', 'bijan robinson'
  ];
  
  const playerNameLower = player.name.toLowerCase();
  const isBreakout = rookieBreakouts.some(name => {
    const nameWords = name.split(' ');
    return nameWords.every(word => playerNameLower.includes(word));
  });
  
  if (isBreakout) {
    // Rookie bonus applied - give rookies/breakouts 20-30% bonus depending on position
    return player.position === 'WR' ? 1.3 : player.position === 'RB' ? 1.25 : 1.2;
  }
  
  return 1.0;
}

export function applyPositionalAdjustments(score: number, player: Player): number {
  let adjustedScore = score;
  
  // Direct age penalties - nuclear option for 31+ players
  const age = player.age || 27;
  if (age >= 31) {
    // Absolutely crush 31+ players
    adjustedScore *= 0.1; // 90% reduction
    
    if (player.name.includes('Diggs') || player.name.includes('Hill')) {
      console.log(`NUCLEAR AGE PENALTY: ${player.name} (age ${age}) score reduced by 90%`);
    }
  } else if (age === 30) {
    // Strong penalty for 30-year-olds  
    adjustedScore *= 0.4; // 60% reduction
  }
  
  if (player.position === 'WR') {
    // Keep WR neutral - rookie bonuses are handling this
    adjustedScore *= 1.0;
  }
  
  if (player.position === 'RB') {
    // RESTORE RB VALUE - especially for young elite RBs
    adjustedScore *= 1.05;
    
    // Volume bonus for high-carry RBs (skip if playerStats not available)
    const carries = (player as any).playerStats?.carries || 0;
    if (carries > 200) {
      adjustedScore *= 1.03;
    }
  }
  
  if (player.position === 'TE') {
    // Boost TE position to fix Brock Bowers issue
    adjustedScore *= 1.1;
  }
  
  return adjustedScore;
}

export function applyRookieBreakoutBonus(adjustedScore: number, player: Player): number {
  try {
    const bonus = calculateRookieBreakoutBonus(player);
    return adjustedScore * bonus;
  } catch (error) {
    // Never fail the scoring pipeline on bad data
    console.warn('Error applying rookie bonus:', error, player);
    return adjustedScore;
  }
}

export function validatePlayerForRanking(player: any, generatedProjections: Record<string, number>): boolean {
  if (!player.full_name || !player.position || !player.team) return false;
  
  // Filter known problematic players
  const suspiciousNames = ['David Moore', 'Practice Squad', 'Chase Cota'];
  if (suspiciousNames.some(name => 
    player.full_name.toLowerCase().includes(name.toLowerCase())
  )) {
    console.log(`FILTERED OUT: ${player.full_name} (known outlier)`);
    return false;
  }
  
  // Minimum projection thresholds to filter out inactive/irrelevant players
  const projection = generatedProjections[String(player.player_id)] || 0;
  const minProjections: Record<string, number> = { 
    QB: 150, RB: 80, WR: 60, TE: 50, K: 80, DEF: 80 
  };
  const minThreshold = minProjections[player.position] || 50;
  
  // Filter players with suspiciously low projections if they have NFL experience
  const yearsExp = player.years_exp || 0;
  if (projection < minThreshold && yearsExp > 2) {
    return false;
  }
  
  return true;
}


